// Dialogs on Radix: focus trap, Escape, focus return and aria wiring come from
// the primitive. Styling only through tokens.
import { AlertDialog as AlertPrimitive, Dialog as DialogPrimitive } from "radix-ui";
import { X } from "lucide-react";
import { useRef, type ComponentProps, type ReactNode } from "react";
import { buttonVariants } from "./button";
import { cn } from "./cn";

export const Dialog = DialogPrimitive.Root;

/**
 * Focus return for CONTROLLED dialogs (opened from a button that is not a
 * Radix Trigger): remember what had focus when the dialog opened and give it
 * back on close, instead of losing focus to <body>.
 */
function useFocusReturn() {
  const previous = useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: () => {
      previous.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    },
    onCloseAutoFocus: (event: Event) => {
      if (previous.current?.isConnected) {
        event.preventDefault();
        previous.current.focus();
      }
    },
  };
}
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const overlay = "fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]";
const panel =
  "ia-animate-in fixed left-1/2 top-[12vh] z-50 grid w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 gap-4 rounded-lg border border-line bg-raised p-5 shadow-overlay focus:outline-none";

export function DialogContent({
  className,
  children,
  title,
  description,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { title: string; description?: ReactNode }) {
  const focus = useFocusReturn();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content className={cn(panel, className)} {...focus} {...props}>
        <div className="grid gap-1 pr-8">
          <DialogPrimitive.Title className="text-base font-semibold">{title}</DialogPrimitive.Title>
          {description ? (
            <DialogPrimitive.Description className="text-sm text-muted">{description}</DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
          )}
        </div>
        {children}
        <DialogPrimitive.Close
          className="absolute right-3 top-3 rounded-sm p-1 text-muted hover:bg-sunken hover:text-fg"
          aria-label="Cerrar"
        >
          <X className="size-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("flex flex-wrap justify-end gap-2", className)} {...props} />;
}

/** Side sheet (mobile navigation, contextual panels). */
export function SheetContent({
  className,
  children,
  title,
  side = "left",
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { title: string; side?: "left" | "right" }) {
  const focus = useFocusReturn();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content
        className={cn(
          "ia-animate-in fixed inset-y-0 z-50 flex w-72 max-w-[85vw] flex-col border-line bg-surface shadow-overlay focus:outline-none",
          side === "left" ? "left-0 border-r" : "right-0 border-l",
          className
        )}
        {...focus}
        {...props}
      >
        <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
        <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ---------------------------------------------------------------- confirmation

interface ConfirmProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: ReactNode;
  readonly confirmLabel: string;
  readonly destructive?: boolean;
  readonly pending?: boolean;
  readonly onConfirm: () => void;
}

/** Confirmation for consequential actions (archive, rollback). Cancel gets initial focus. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmProps) {
  const focus = useFocusReturn();
  return (
    <AlertPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className={overlay} />
        <AlertPrimitive.Content className={panel} {...focus}>
          <div className="grid gap-1">
            <AlertPrimitive.Title className="text-base font-semibold">{title}</AlertPrimitive.Title>
            <AlertPrimitive.Description className="text-sm text-muted">{description}</AlertPrimitive.Description>
          </div>
          <div className="flex justify-end gap-2">
            <AlertPrimitive.Cancel className={buttonVariants({ variant: "secondary" })}>Cancelar</AlertPrimitive.Cancel>
            <AlertPrimitive.Action
              className={buttonVariants({ variant: destructive ? "danger" : "primary" })}
              disabled={pending}
              onClick={(event) => {
                event.preventDefault();
                onConfirm();
              }}
            >
              {confirmLabel}
            </AlertPrimitive.Action>
          </div>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  );
}
